import {
  EXEMPTION_STATUSES,
  FRICTION_STATUSES,
  LEAD_STATUS_LABELS,
  TEST_RESULTS,
  type ExemptionStatus,
  type FrictionStatus,
  type TestResult,
  type UpsertUnitLeadStatusInput,
} from '@sitewise/shared';
import {
  Card,
  CardContent,
  Input,
  Label,
  StatPill,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@sitewise/ui';
import { Info } from 'lucide-react';
import { useId, useState } from 'react';

import { LEAD_STATUS_TONE } from '../lead-status.js';

import { DocumentSlot } from './document-slot.js';
import { SegmentedControl } from './segmented-control.js';

import type { UnitLeadState } from '../../../../api/lead.js';

interface Props {
  unitId: string;
  state: UnitLeadState;
  /** Save a partial patch. The parent owns the optimistic-update +
   *  sync-indicator tracking — this component only knows what the
   *  user clicked. */
  onSave: (patch: UpsertUnitLeadStatusInput) => Promise<void>;
  disabled?: boolean;
}

const FRICTION_OPTIONS = FRICTION_STATUSES.map((v) => ({
  value: v,
  label: v === 'not_addressed' ? 'Not addressed' : 'Clean',
}));

const EXEMPTION_OPTIONS = EXEMPTION_STATUSES.map((v) => ({
  value: v,
  label:
    v === 'none'
      ? 'None'
      : v === 'applied_pending'
        ? 'Applied (pending)'
        : v === 'approved'
          ? 'Approved'
          : 'Rejected',
}));

const TEST_OPTIONS = TEST_RESULTS.map((v) => ({
  value: v,
  label: v === 'not_tested' ? 'Not tested' : v === 'negative' ? 'Negative' : 'Positive',
}));

/**
 * The 2×2 status grid: friction, exemption, test result, and the
 * read-only derived status pill. Each control auto-saves on change;
 * conditional sub-fields (positive count, XRF date, attachment slots)
 * appear inline beneath the segmented control they depend on.
 *
 * The derived pill is computed server-side but we also reflect
 * optimistic local state via the `state` prop the parent passes —
 * so a successful PUT response updates the pill immediately without
 * a refetch round trip.
 */
export function StatusPanel({ unitId, state, onSave, disabled }: Props) {
  const positiveCountId = useId();
  const xrfDateId = useId();

  // Local controlled state for the inline numeric / date inputs.
  // Keep them as strings so partial edits don't fight the parent.
  const [positiveDraft, setPositiveDraft] = useState<string>(
    state.status.positiveCount != null ? String(state.status.positiveCount) : '',
  );
  const [xrfDraft, setXrfDraft] = useState<string>(state.status.latestXrfDate ?? '');

  // Sync local drafts when the parent state changes (e.g. after a
  // refetch). We don't want stale local values to obscure the truth.
  if (state.status.positiveCount != null && positiveDraft === '' && !disabled) {
    setPositiveDraft(String(state.status.positiveCount));
  }
  if (state.status.latestXrfDate && xrfDraft === '' && !disabled) {
    setXrfDraft(state.status.latestXrfDate);
  }

  async function handleFriction(next: FrictionStatus) {
    await onSave({ friction_status: next });
  }

  async function handleExemption(next: ExemptionStatus) {
    const patch: UpsertUnitLeadStatusInput = { exemption_status: next };
    // Clearing back from approved → anything else removes the letter
    // attachment to keep the cross-field invariant happy.
    if (next !== 'approved' && state.status.exemptionLetterDocumentId) {
      patch.exemption_letter_document_id = null;
    }
    await onSave(patch);
  }

  async function handleTestResult(next: TestResult) {
    const patch: UpsertUnitLeadStatusInput = { test_result: next };
    if (next !== 'positive') {
      patch.positive_count = null;
    } else if (state.status.positiveCount == null) {
      // Starting from 1 lets the user "click positive → see the
      // count field → bump it"; the API will reject 0.
      patch.positive_count = 1;
      setPositiveDraft('1');
    }
    await onSave(patch);
  }

  async function commitPositiveCount() {
    if (state.status.testResult !== 'positive') return;
    const parsed = positiveDraft.trim() === '' ? null : Number(positiveDraft);
    if (parsed == null || !Number.isFinite(parsed) || parsed < 1) return;
    if (parsed === state.status.positiveCount) return;
    await onSave({ positive_count: Math.floor(parsed) });
  }

  async function commitXrfDate() {
    const next = xrfDraft.trim() === '' ? null : xrfDraft;
    if (next === state.status.latestXrfDate) return;
    if (next !== null && !/^\d{4}-\d{2}-\d{2}$/.test(next)) return;
    await onSave({ latest_xrf_date: next });
  }

  return (
    <Card>
      <CardContent className="space-y-5 p-5">
        <div className="grid gap-5 sm:grid-cols-2">
          {/* Friction surfaces */}
          <Panel
            label="Friction surfaces"
            help="Doors, windows, and other high-friction surfaces."
          >
            <SegmentedControl
              ariaLabel="Friction surfaces"
              value={state.status.frictionStatus}
              onChange={handleFriction}
              options={FRICTION_OPTIONS}
              disabled={disabled}
            />
          </Panel>

          {/* Exemption */}
          <Panel
            label="Exemption"
            help="Track applications submitted under EPA / NYC rules."
          >
            <SegmentedControl
              ariaLabel="Exemption"
              value={state.status.exemptionStatus}
              onChange={handleExemption}
              options={EXEMPTION_OPTIONS}
              disabled={disabled}
            />
            {state.status.exemptionStatus === 'approved' ? (
              <div className="mt-3">
                <DocumentSlot
                  unitId={unitId}
                  document={state.exemptionLetter}
                  kind="lead_exemption_letter"
                  label="Exemption letter"
                  helpText="Attach the approval letter on file."
                  disabled={disabled}
                  onAttached={(id) =>
                    onSave({ exemption_letter_document_id: id })
                  }
                  onDetached={() =>
                    onSave({ exemption_letter_document_id: null })
                  }
                />
              </div>
            ) : null}
          </Panel>

          {/* Test result */}
          <Panel
            label="Test result"
            help="XRF reading. Positive requires a count of locations."
          >
            <SegmentedControl
              ariaLabel="Test result"
              value={state.status.testResult}
              onChange={handleTestResult}
              options={TEST_OPTIONS}
              disabled={disabled}
            />
            {state.status.testResult === 'positive' ? (
              <div className="mt-3">
                <Label htmlFor={positiveCountId} className="text-xs">
                  Positive findings
                </Label>
                <Input
                  id={positiveCountId}
                  type="number"
                  min={1}
                  step={1}
                  value={positiveDraft}
                  onChange={(e) => setPositiveDraft(e.target.value)}
                  onBlur={commitPositiveCount}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void commitPositiveCount();
                    }
                  }}
                  disabled={disabled}
                  className="mt-1 max-w-[120px]"
                />
              </div>
            ) : null}
            {state.status.testResult !== 'not_tested' ? (
              <div className="mt-3">
                <Label htmlFor={xrfDateId} className="text-xs">
                  XRF performed on
                </Label>
                <Input
                  id={xrfDateId}
                  type="date"
                  value={xrfDraft}
                  onChange={(e) => setXrfDraft(e.target.value)}
                  onBlur={commitXrfDate}
                  disabled={disabled}
                  className="mt-1 max-w-[180px]"
                />
              </div>
            ) : null}
            {state.status.testResult !== 'not_tested' ? (
              <div className="mt-3">
                <DocumentSlot
                  unitId={unitId}
                  document={state.latestXrfReport}
                  kind="lead_xrf_report"
                  label="XRF report"
                  helpText="Attach the inspector's PDF."
                  disabled={disabled}
                  onAttached={(id) =>
                    onSave({ latest_xrf_report_document_id: id })
                  }
                  onDetached={() =>
                    onSave({ latest_xrf_report_document_id: null })
                  }
                />
              </div>
            ) : null}
          </Panel>

          {/* Derived overall status */}
          <Panel
            label="Overall status"
            help="Computed from the three fields on the left."
          >
            <div className="flex items-center gap-2">
              <StatPill
                tone={LEAD_STATUS_TONE[state.leadStatus]}
                value={LEAD_STATUS_LABELS[state.leadStatus]}
                withDot
              />
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="How this is computed"
                      className="rounded-full text-fg-muted hover:text-fg"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs leading-relaxed">
                    Approved exemption &gt; Negative test &gt; Positive test &gt;
                    Rejected exemption &gt; Pending exemption &gt; Friction clean
                    only &gt; Untested.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {state.leadStatusUpdatedAt ? (
              <p className="mt-2 text-xs text-fg-muted">
                Updated {new Date(state.leadStatusUpdatedAt).toLocaleString()}
              </p>
            ) : null}
          </Panel>
        </div>
      </CardContent>
    </Card>
  );
}

function Panel({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {label}
        </span>
        {help ? (
          <span className="text-[11px] text-fg-muted">{help}</span>
        ) : null}
      </div>
      {children}
    </div>
  );
}
